/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Dashboard Page of this app.
 */
import {
  Box,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useFetchData } from "../../components/hooks/useFetchData";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const DashboardPage = () => {
  const [minLoading, setMinLoading] = useState(true);
  const role = useSelector((state) => state.auth.user?.role);
  const [dashboardFollowupList, setDashboardFollowupList] = useState({
    overduefollowup: [],
    todayfollowup: [],
    upcomingfollowup: [],
  });
  const navigate = useNavigate();
  const { fetchData, loading } = useFetchData();
  const { enqueueSnackbar } = useSnackbar();

  const dashboardFollowup = async () => {
    try {
      const result = await fetchData("dashboardfollowup");
      if (result?.code === 200) {
        setDashboardFollowupList(result.data);
      } else {
        enqueueSnackbar(
          result?.message || "Failed to fetch dashboard follow-up data.",
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar("Error while fetching dashboard follow-up data.", {
        variant: "error",
      });
    }
  };

  React.useEffect(() => {
    dashboardFollowup();
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = minLoading || loading["dashboardfollowup"];

  const renderFollowUpList = (title, titleColor, items) => (
    <Box
      minWidth={360}
      flex={1}
      borderRadius="12px"
      px="8px"
      py={3}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      mb={2}
    >
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={700} fontSize={20} color={titleColor}>
            {title}
          </Typography>
        )}
      </Stack>
      <Divider />
      <Box
        sx={{
          flex: 1,
          width: "100%",
          overflow: "auto",
          pb: 1,
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 360 }} aria-label="simple table">
              <TableBody>
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <TableRow key={index} sx={{ p: 0 }}>
                      <TableCell sx={{ p: 0, borderBottom: 0 }}>
                        {item.followupdate
                          ? dayjs(item.followupdate).format("MMM D,YYYY")
                          : ""}
                      </TableCell>
                      <TableCell sx={{ p: 0, borderBottom: 0 }}>
                        {item.name}
                      </TableCell>
                      {role !== "agent" ? (
                        <TableCell align="right" sx={{ p: 0, borderBottom: 0 }}>
                          <IconButton
                            onClick={() => {
                              item.leadconversiondate === null
                                ? navigate(
                                    `/leads/lead-details/${item.candidateuuid}`
                                  )
                                : navigate(
                                    `/candidates/candidate-details/${item.candidateuuid}`
                                  );
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                ) : (
                  <Typography sx={{ mt: 2 }} fontSize={16} color="#999">
                    No data available.
                  </Typography>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow={{
          xs: "auto",
          md: "hidden",
        }}
      >
        {isLoading ? (
          <Skeleton sx={{ mt: 3 }} variant="rounded" width={390} height={24} />
        ) : (
          <Typography
            sx={{ mt: 2, mb: 0.5 }}
            fontWeight={700}
            fontSize={24}
            color="#222222"
          >
            Highlights
          </Typography>
        )}
        {!isLoading && <Divider />}
        <Stack
          display="flex"
          direction="row"
          flexWrap="wrap"
          overflow={{ xs: "unset", md: "hidden" }}
          mt={1}
          gap={2}
        >
          {renderFollowUpList(
            "Overdue",
            "#D64D1F",
            dashboardFollowupList.overduefollowup
          )}
          {renderFollowUpList(
            "Today",
            "#448526",
            dashboardFollowupList.todayfollowup
          )}
          {renderFollowUpList(
            "Upcoming",
            "#D49628",
            dashboardFollowupList.upcomingfollowup
          )}
        </Stack>
      </Box>
    </>
  );
};

export default DashboardPage;
